import ShopifyService from './ajax-service';

export const CART_EVENTS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  LOADING: 'LOADING',
  FINISHED: 'FINISHED',
};

(function () {
  const originalFetch = window.fetch;
  const ongoingRequests = new Set();
  let addRequestInProgress = false; // Flag to indicate if an add.js request is in progress
  let debounceTimeout = null;
  const DEBOUNCE_DELAY = 500; // Adjust this delay as needed

  function scheduleCartUpdate() {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    debounceTimeout = setTimeout(() => {
      if (addRequestInProgress) {
        CartServiceSingleton.get().finally(() => {
          addRequestInProgress = false; // Reset the flag after update is complete
        });
      }
    }, DEBOUNCE_DELAY);
  }

  function isCartRelatedRequest(url) {
    return url.includes('add.js') || url.includes('/cart.js'); // Customize this check as needed
  }

  window.fetch = function (...args) {
    const [url] = args;
    const request = originalFetch.apply(this, args);

    // Track all ongoing requests
    ongoingRequests.add(request);

    // If the request is an add.js request, set the flag
    if (isCartRelatedRequest(url)) {
      addRequestInProgress = true;
    }

    request
      .finally(() => {
        // Remove the request from ongoing requests
        ongoingRequests.delete(request);

        // Check if there are no more ongoing requests
        if (ongoingRequests.size === 0) {
          scheduleCartUpdate(); // Schedule update only when all requests are done
        }
      });

    return request;
  };
})();
class CartService {
  constructor() {
    this.cart = {
      id: null,
      subtotal: {
        amount: 0,
        currencyCode: window.TMW?.localization?.currencyCode,
      },
      lines: [],
    };
    this.loading = false;
    this.observers = [];

    this.get();

    // For backwards compatibility we will listen for events
    // However, we should avoid using events and import/call methods on CartService directly
    document.addEventListener('cart:add', (e) => this.add(e.detail?.payload));
    document.addEventListener('cart:updateAttributes', (e) =>
      this.updateAttributes(e.detail?.payload),
    );
    this.mode = ShopifyService.name;
  }

  async callShopify(method, event, ...params) {
    this.loading = true;
    this.notify(CART_EVENTS.LOADING);
    const cart = await ShopifyService[method](this.cart, ...params);
    const { items: extendedLines } = await ShopifyService.getExtendedLines()

    const combined = cart.items.map(item => {
      let obj = {}
      extendedLines.forEach(item2 => {
        if (item.id == item2.id) {
          obj = item2
        }
      })
      return { ...item, ...obj }
    })

    if (cart) this.cart = { ...cart, items: combined };
    if (event) this.notify(event);
    this.loading = false;
    this.notify(CART_EVENTS.FINISHED);
    return cart;
  }

  async get(silent = false) {
    return this.callShopify('get', silent ? null : CART_EVENTS.CREATE);
  }

  async add(lines, silent = false) {
    return this.callShopify('add', silent ? null : CART_EVENTS.UPDATE, lines);
  }

  async remove(lines, silent = false) {
    return this.callShopify(
      'remove',
      silent ? null : CART_EVENTS.UPDATE,
      lines,
    );
  }

  async update(lines, silent = false) {
    return this.callShopify(
      'update',
      silent ? null : CART_EVENTS.UPDATE,
      lines,
    );
  }

  async change(lines, silent = false) {
    return this.callShopify(
      'change',
      silent ? null : CART_EVENTS.UPDATE,
      lines,
    );
  }

  async updateAttributes(attributes, silent = true) {
    return this.callShopify(
      'updateAttributes',
      silent ? null : CART_EVENTS.UPDATE,
      attributes,
    );
  }

  notify(event) {
    this.observers.forEach((o) => o.call(o, event));
  }

  addListener(observer) {
    this.observers.push(observer);
  }

  removeListener(observer) {
    this.observers = this.observers.filter((o) => o !== observer);
  }
}

const CartServiceSingleton = new CartService();
export default CartServiceSingleton;
